import dayjs from 'dayjs';

export const transformAllocationMisResponse = (response) => {
  if (!response) return;
};

const ALLOCATION_MIS_PROJECT_ROW_BG = [
  'var(--allocation-mis-project-row-bg-primary)',
  'var(--allocation-mis-project-row-bg-secondary)',
];
const ALLOCATION_MIS_TOTAL_ROW_BG = 'var(--allocation-mis-total-row-bg)';
const ALLOC_MIS_PROJECT_ROW_CLASSES = [
  'allocation-mis-row--project-primary',
  'allocation-mis-row--project-secondary',
];
const ALLOCATION_MIS_TOTAL_ROW_CLASS_NAME = 'allocation-mis-row--total';
const ALLOCATION_MIS_SORT_FIELD_PREFIX = '__allocationMisSort__';
const ALLOCATION_MIS_ROW_KEY_FIELD = '__tuRowKey';

const EMPLOYEE_IDENTITY_FIELDS = new Set([
  'employeeName',
  'employeeVertical',
  'employeeDesignation',
]);

const TOTAL_ROW_BLANK_FIELDS = new Set([
  'employeeId',
  'employeeVertical',
  'employeeDesignation',
  'employeeDepartment',
  'projectName',
  'resourceType',
  'skills',
  'allocationStartDate',
  'allocationEndDate',
  'numberOfDays',
]);

const EXCLUDED_EXPORT_FIELDS = new Set([
  'activeDateRange',
  'allocationStartDateRange',
  'allocationEndDateRange',
  'primarySkill',
  'secondarySkill',
]);

const EXPORT_DATE_FORMAT = 'DD-MMM-YYYY';

// dynamic-table does not auto-format cell values from a column's `dateFormat` config, so raw
// API date strings must be reformatted for display ourselves (mirrors the export formatting).
export const formatAllocationMisDisplayDate = (value, format = EXPORT_DATE_FORMAT) => {
  const rawValue = `${value ?? ''}`.trim();
  if (!rawValue || rawValue === '-') return rawValue;

  const parsedDate = dayjs(rawValue);
  return parsedDate.isValid() ? parsedDate.format(format) : rawValue;
};

const formatExportDateValue = (value) => formatAllocationMisDisplayDate(value, EXPORT_DATE_FORMAT);

const getNormalizedAllocationMisValue = (value) => `${value ?? ''}`.trim();

const getAllocationMisEmployeeKey = (row = {}, index = 0) => {
  const keyParts = [
    row?.emp_id,
    row?.employeeId,
    row?.employeeName,
    row?.employeeVertical,
    row?.employeeDesignation,
  ]
    .map((value) => `${value ?? ''}`.trim())
    .filter(Boolean);

  return keyParts.length > 0 ? keyParts.join('|') : `allocation-mis-row-${index}`;
};

const getAllocationMisRowKey = (row = {}, index = 0, employeeKey = '') => {
  const rowParts = [
    employeeKey,
    row?.rowType,
    row?.projectId,
    row?.projectName,
    row?.resourceType,
    row?.allocationStartDate,
    row?.allocationEndDate,
    row?.allocationPercent,
    index,
  ].map(getNormalizedAllocationMisValue);

  return `allocation-mis-${rowParts.join('|')}`;
};

export const getAllocationMisSortFieldName = (field = '') => {
  const normalizedField = `${field ?? ''}`.trim();
  if (!normalizedField || normalizedField.startsWith(ALLOCATION_MIS_SORT_FIELD_PREFIX)) return normalizedField;
  return `${ALLOCATION_MIS_SORT_FIELD_PREFIX}${normalizedField}`;
};

export const mapAllocationMisSortMenuItems = (menuItems = [], field = '') => {
  const sortFieldName = getAllocationMisSortFieldName(field);

  if (!sortFieldName || !Array.isArray(menuItems)) return menuItems;

  return menuItems.map((menuItem) => {
    if (!Array.isArray(menuItem?.items)) return menuItem;

    return {
      ...menuItem,
      items: menuItem.items.map((item) => {
        if (item?.type !== 'sort') return item;

        return {
          ...item,
          field: getAllocationMisSortFieldName(item.field || field),
          serverSideSorting: true,
          valueType: 'string',
        };
      }),
    };
  });
};

export const preserveAllocationMisServerOrder = (columns = [], rows = []) => {
  const sortFields = new Set();
  const tableConfiguration = (Array.isArray(columns) ? columns : []).map((column) => {
    const menuItems = mapAllocationMisSortMenuItems(column.menuItems, column.field);
    menuItems?.forEach((menu) => menu.items?.forEach((item) => {
      if (item.type === 'sort') sortFields.add(item.field);
    }));
    return { ...column, menuItems };
  });


  const localSortValues = Object.fromEntries([...sortFields].map((field) => [field, '']));
  const tableData = (Array.isArray(rows) ? rows : []).map((row) => ({ ...row, ...localSortValues }));
  return { tableConfiguration, tableData };
};

const buildAllocationMisSortValues = (row = {}, sortSourceRow = {}) => {
  const sortValues = {};

  Object.entries(row || {}).forEach(([field, value]) => {
    if (!field || field === ALLOCATION_MIS_ROW_KEY_FIELD || field.startsWith(ALLOCATION_MIS_SORT_FIELD_PREFIX)) return;

    const sortSourceValue = Object.prototype.hasOwnProperty.call(sortSourceRow, field)
      ? sortSourceRow[field]
      : value;

    sortValues[getAllocationMisSortFieldName(field)] = sortSourceValue;
  });

  return sortValues;
};

/**
 * Checks if rows need regrouping by detecting if Total rows are separated from their employee rows.
 * Returns true if regrouping is needed (e.g., after sorting).
 */
const needsRegrouping = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  let currentEmployeeId = '';

  for (let index = 0; index < rows.length; index++) {
    const row = rows[index];
    const employeeId = getNormalizedAllocationMisValue(row?.employeeId) || currentEmployeeId;

    if (row?.rowType === 'total') {
      if (!employeeId || employeeId !== currentEmployeeId) {
        return true;
      }

      currentEmployeeId = '';
      continue;
    }

    if (employeeId) {
      if (currentEmployeeId && employeeId !== currentEmployeeId) return true;
      currentEmployeeId = employeeId;
    } else {
      if (!currentEmployeeId) return true;
    }
  }

  return false;
};

/**
 * Re-groups rows to ensure Total rows follow their respective employee project rows.
 * This is needed because server-side sorting can separate Total rows from their parent rows.
 */
const regroupAllocationMisRows = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const employeeGroups = new Map();
  const employeeOrder = []; // Track the order of first appearance
  let currentEmployeeId = '';

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;

    const employeeId = getNormalizedAllocationMisValue(row?.employeeId) || currentEmployeeId;
    if (!employeeId) return;

    if (!employeeGroups.has(employeeId)) {
      employeeGroups.set(employeeId, { projectRows: [], totalRow: null });
      employeeOrder.push(employeeId);
    }

    const group = employeeGroups.get(employeeId);
    if (row?.rowType === 'total') {
      group.totalRow = row;
      currentEmployeeId = '';
    } else {
      group.projectRows.push(row);
      currentEmployeeId = employeeId;
    }
  });

  // Rebuild rows maintaining the order of first appearance
  const regroupedRows = [];
  employeeOrder.forEach((employeeId) => {
    const group = employeeGroups.get(employeeId);
    regroupedRows.push(...group.projectRows);
    if (group.totalRow) {
      regroupedRows.push(group.totalRow);
    }
  });

  return regroupedRows;
};

export const decorateAllocationMisRows = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  // Only re-group if rows are out of order (e.g., after sorting)
  // When filtering, the API returns correctly grouped data
  const regroupedRows = needsRegrouping(rows) ? regroupAllocationMisRows(rows) : rows;

  let lastEmployeeKey = '';
  let projectRowIndexForEmployee = 0;
  let lastProjectRowForEmployee = null;
  let firstProjectRowForEmployee = null;
  let currentEmployeeKey = '';

  return regroupedRows.map((row, index) => {
    if (!row || typeof row !== 'object') return row;

    const rowEmployeeKey = getAllocationMisEmployeeKey(row, index);
    const hasEmployeeIdentity = [
      row?.emp_id,
      row?.employeeId,
      row?.employeeName,
      row?.employeeVertical,
      row?.employeeDesignation,
    ].some((value) => getNormalizedAllocationMisValue(value));
    const employeeKey = hasEmployeeIdentity ? rowEmployeeKey : currentEmployeeKey || rowEmployeeKey;

    if (row?.rowType === 'total') {
      const blankedTotalRow = { ...row };
      const totalRowSortValues = buildAllocationMisSortValues(
        row,
        firstProjectRowForEmployee || lastProjectRowForEmployee || row,
      );
      const rowKey = getAllocationMisRowKey(row, index, employeeKey);

      TOTAL_ROW_BLANK_FIELDS.forEach((field) => {
        blankedTotalRow[field] = '';
      });

      lastEmployeeKey = '';
      projectRowIndexForEmployee = 0;
      lastProjectRowForEmployee = null;
      firstProjectRowForEmployee = null;
      currentEmployeeKey = '';

      return {
        ...blankedTotalRow,
        ...totalRowSortValues,
        [ALLOCATION_MIS_ROW_KEY_FIELD]: rowKey,
        employeeName: 'Total',
        style: {
          ...(row.style || {}),
          backgroundColor: ALLOCATION_MIS_TOTAL_ROW_BG,
        },
        allocationMisDisplay: {
          ...(row.allocationMisDisplay || {}),
          isTotalRow: true,
          projectRowIndex: -1,
          hideEmployeeIdentity: false,
        },
        rowConfiguration: {
          ...(row.rowConfiguration || {}),
          className: ALLOCATION_MIS_TOTAL_ROW_CLASS_NAME,
          backgroundColor: ALLOCATION_MIS_TOTAL_ROW_BG,
        },
      };
    }

    const isNewEmployeeGroup = employeeKey !== lastEmployeeKey;

    if (isNewEmployeeGroup) {
      projectRowIndexForEmployee = 0;
      lastEmployeeKey = employeeKey;
      firstProjectRowForEmployee = row;
    }

    lastProjectRowForEmployee = row;
    currentEmployeeKey = employeeKey;

    const nextProjectRowIndex = projectRowIndexForEmployee;
    projectRowIndexForEmployee += 1;
    const projectRowClassName = ALLOC_MIS_PROJECT_ROW_CLASSES[
      nextProjectRowIndex % ALLOC_MIS_PROJECT_ROW_CLASSES.length
    ];

    return {
      ...row,
      ...buildAllocationMisSortValues(row, firstProjectRowForEmployee || row),
      employeeId: row.employeeId || firstProjectRowForEmployee?.employeeId || '',
      resourceCategory: row.resourceCategory || firstProjectRowForEmployee?.resourceCategory || '',
      [ALLOCATION_MIS_ROW_KEY_FIELD]: getAllocationMisRowKey(row, index, employeeKey),
      style: {
        ...(row.style || {}),
        backgroundColor: ALLOCATION_MIS_PROJECT_ROW_BG[nextProjectRowIndex % ALLOCATION_MIS_PROJECT_ROW_BG.length],
      },
      allocationMisDisplay: {
        ...(row.allocationMisDisplay || {}),
        isTotalRow: false,
        projectRowIndex: nextProjectRowIndex,
        hideEmployeeIdentity: nextProjectRowIndex > 0,
        hideTableLine: nextProjectRowIndex > 0,
      },
      rowConfiguration: {
        ...(row.rowConfiguration || {}),
        className: projectRowClassName,
        backgroundColor: ALLOCATION_MIS_PROJECT_ROW_BG[nextProjectRowIndex % ALLOCATION_MIS_PROJECT_ROW_BG.length],
      },
    };
  });
};

export const getAllocationMisCellValue = (row = {}, field, value, dateFormat) => {
  if (!field) return value ?? '';

  const displayState = row?.allocationMisDisplay || {};
  const rawValue = value ?? row?.[field] ?? '';
  const displayValue = dateFormat ? formatAllocationMisDisplayDate(rawValue, dateFormat) : rawValue;

  if (field === 'totalEfforts') {
    return formatTotalEffortsDisplayValue(rawValue);
  }

  if (displayState.isTotalRow) {
    if (field === 'employeeName') return 'Total';
    if (TOTAL_ROW_BLANK_FIELDS.has(field)) return '';
    return displayValue;
  }

  if (displayState.hideEmployeeIdentity && EMPLOYEE_IDENTITY_FIELDS.has(field)) {
    return '';
  }

  return displayValue;
};

const getTotalEffortsNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return value ?? '';

  const numericValue = Number(`${value}`.replace(/,/g, ''));
  return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : value;
};

const formatTotalEffortsDisplayValue = (value) => {
  const numericValue = getTotalEffortsNumericValue(value);
  return typeof numericValue === 'number' ? numericValue.toFixed(2) : numericValue;
};

const EXPORT_STATIC_COLUMNS = [
  { field: 'section', header: 'Section', visible: true, type: 'string', width: 80 },
  { field: 'employeeName', header: 'Employee Name', visible: true, type: 'string', width: 160 },
  { field: 'employeeId', header: 'Employee / TBH ID', visible: true, type: 'string', width: 100 },
  { field: 'resourceCategory', header: 'Resource Category', visible: true, type: 'string', width: 120 },
  { field: 'tbhId', header: 'TBH Id', visible: true, type: 'string', width: 100 },
  { field: 'employeeVertical', header: 'Employee/TBH Vertical', visible: true, type: 'string', width: 140 },
  { field: 'emailId', header: 'Employee Email', visible: true, type: 'string', width: 200 },
  { field: 'employeeDesignation', header: 'Employee/TBH Designation', visible: true, type: 'string', width: 160 },
  { field: 'employeeDepartment', header: 'Employee / TBH Department', visible: true, type: 'string', width: 160 },
  { field: 'projectName', header: 'Project Name', visible: true, type: 'string', width: 160 },
  { field: 'projectId', header: 'Project Id', visible: true, type: 'string', width: 100 },
  { field: 'projectVertical', header: 'Project Vertical', visible: true, type: 'string', width: 130 },
  { field: 'resourceType', header: 'Resource Type', visible: true, type: 'string', width: 120 },
  { field: 'skills', header: 'Skills', visible: true, type: 'string', width: 120 },
  { field: 'allocationStartDate', header: 'Allocation Start Date', visible: true, type: 'string', width: 110 },
  { field: 'allocationEndDate', header: 'Allocation End Date', visible: true, type: 'string', width: 110 },
  { field: 'allocationPercent', header: 'Allocation %', visible: true, type: 'number', width: 100 },
  { field: 'resourceAllocationComment', header: 'Comments', visible: true, type: 'string', width: 180 },
  { field: 'totalEfforts', header: 'Total Efforts', visible: true, type: 'number', width: 100 },
  { field: 'numberOfDays', header: 'Number of Days', visible: true, type: 'number', width: 120 },
  { field: 'totalDays', header: 'Total Days', visible: true, type: 'number', width: 100 },
];

const normalizeExportNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return value ?? '';

  const numericValue = Number(`${value}`.replace(/,/g, ''));
  return Number.isFinite(numericValue) ? numericValue : value;
};

const isAllocationMisDateField = (field = '') => /^\d{4}-\d{2}-\d{2}$/.test(`${field}`.trim());

const formatAllocationMisExportDailyValue = (value) => {
  const normalizedValue = normalizeExportNumericValue(value);
  return typeof normalizedValue === 'number' ? normalizedValue.toFixed(2) : normalizedValue;
};

const normalizeExportNumericColumns = (row = {}, tableConfiguration = []) => {
  const normalizedRow = { ...row };

  tableConfiguration.forEach((column) => {
    const hasColumnValue = Object.prototype.hasOwnProperty.call(normalizedRow, column?.field);

    if (column?.type === 'number' && column?.field && hasColumnValue) {
      normalizedRow[column.field] = column.field === 'totalEfforts'
        ? getTotalEffortsNumericValue(normalizedRow[column.field])
        : normalizeExportNumericValue(normalizedRow[column.field]);
    }
  });

  return normalizedRow;
};

const EXPORT_STATIC_FIELD_NAMES = new Set([
  'projectVertical', 'employeeType', 'employeeExperience', 'skills',
  'allocationStartDate', 'allocationEndDate',
  'employeeName', 'employeeId', 'employeeVertical', 'employeeDesignation',
  'employeeDepartment', 'projectName', 'resourceType', 'numberOfDays', 'totalDays',
  'allocationPercent', 'rowType', 'emailId', 'projectId', 'resourceCategory', 'tbhId',
  'totalEfforts', 'resourceAllocationComment',
]);

export const transformAllocMisExport = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;

  const tableConfiguration = Array.isArray(payload?.tableConfiguration) ? payload.tableConfiguration : [];
  const tableData = Array.isArray(payload?.tableData) ? payload.tableData : [];
  const dailyDateFields = [...new Set([
    ...(Array.isArray(payload?.dates) ? payload.dates : []),
    ...tableConfiguration.map((column) => column?.field).filter(isAllocationMisDateField),
  ])];

  const dynamicColumns = tableConfiguration.filter(
    (col) => (
      col?.field
      && !EXCLUDED_EXPORT_FIELDS.has(col.field)
      && !EXPORT_STATIC_FIELD_NAMES.has(col.field)
      && !dailyDateFields.includes(col.field)
    ),
  ).map((col) => ({ ...col, visible: true }));

  const dailyDateColumns = dailyDateFields.map((date) => ({
    field: date,
    header: formatAllocationMisDisplayDate(date),
    visible: true,
    type: 'string',
    numeric: false,
    align: 'right',
    width: 90,
  }));

  const exportTableConfiguration = [...EXPORT_STATIC_COLUMNS, ...dynamicColumns, ...dailyDateColumns];

  const buildSpacerRow = () => exportTableConfiguration.reduce((acc, column) => {
    if (column?.field) acc[column.field] = '';
    return acc;
  }, {});

  const employeeIdentityFields = [
    'employeeName',
    'employeeId',
    'resourceCategory',
    'tbhId',
    'employeeVertical',
    'emailId',
    'employeeDesignation',
    'employeeDepartment',
  ];
  let currentEmployeeDetails = {};

  const exportTableData = decorateAllocationMisRows(tableData).reduce((result, row) => {
    if (row?.rowType !== 'total') {
      const isFirstProjectRow = row?.allocationMisDisplay?.projectRowIndex === 0;
      if (isFirstProjectRow) currentEmployeeDetails = {};
      employeeIdentityFields.forEach((field) => {
        const value = row?.[field];
        if (isFirstProjectRow && Object.prototype.hasOwnProperty.call(row, field)) {
          currentEmployeeDetails[field] = value ?? '';
        } else if (value !== null && value !== undefined && `${value}`.trim() !== '') {
          currentEmployeeDetails[field] = value;
        }
      });
    }

    const normalizedRow = {
      ...row,
      section: row?.rowType === 'total' ? 'Total' : 'Project',
      allocationStartDate: formatExportDateValue(row?.allocationStartDate),
      allocationEndDate: formatExportDateValue(row?.allocationEndDate),
    };

    if (row?.allocationMisDisplay?.isTotalRow) {
      TOTAL_ROW_BLANK_FIELDS.forEach((field) => {
        normalizedRow[field] = '';
      });
    } else if (row?.allocationMisDisplay?.hideEmployeeIdentity) {
      EMPLOYEE_IDENTITY_FIELDS.forEach((field) => {
        normalizedRow[field] = '';
      });
    }

    dailyDateFields.forEach((date) => {
      if (Object.prototype.hasOwnProperty.call(normalizedRow, date)) {
        normalizedRow[date] = formatAllocationMisExportDailyValue(normalizedRow[date]);
      }
    });

    Object.assign(normalizedRow, currentEmployeeDetails);
    result.push(normalizeExportNumericColumns(normalizedRow, exportTableConfiguration));

    if (row?.rowType === 'total') {
      result.push(buildSpacerRow());
    }

    return result;
  }, []);

  return {
    ...payload,
    tableConfiguration: exportTableConfiguration,
    tableData: exportTableData,
  };
};
